/**
 * The project-shape wizard (`KLQ-20260825-007`), in English.
 *
 * **The word "Generate" is not in this file and must not enter it.** It was the label on the
 * button this screen replaces, and it was the whole problem: it says nothing about what is
 * produced, reads as cheap, and gave no reason to press it. An architect divides a project
 * into blocks and floors and decides which drawings each one carries — so that is what the
 * copy says, in those words.
 *
 * Two lines carry more weight than they look:
 *
 * `check.intro` is the reason the preview step exists at all. A generated code goes into the
 * address of every drawing beneath it and locks on the first published version, so the number
 * on that screen is the one thing worth reading before pressing anything.
 *
 * `levels.addsOnly` is a promise made *before* the form on a project that already has a shape,
 * because "run the setup again" is otherwise a sentence somebody reads as "replace what is
 * there". Nothing this screen calls can move or remove a level.
 */

export const projectSetupEn = {
  'page.title': 'The shape of this project',
  'page.subtitle':
    'Blocks, floors, units, and which drawings each of them carries. It takes a minute, and you can change it later.',
  // The editor's header. The wizard's own subtitle promises a minute and an ending; this one
  // promises neither, because somebody who came back has one change in mind and no patience
  // for a tour.
  'shape.subtitle':
    'Pick the level you want to change. Every operation here says what it will do before it does it.',

  'status.loading': 'Loading the project…',
  'status.readOnly':
    'You can see this project but not change how it is divided. Ask whoever runs it.',

  'step.type': 'Building type',
  'type.title': 'What kind of building?',
  'type.intro':
    'Your answer sets up a structure we show you before anything is written. It can be changed or extended later.',
  'template.familyHouse': 'Family house',
  'template.familyHouse.hint': 'One building, floors 1.PP, 1.NP and 2.NP',
  'template.other': 'Other',
  'template.other.hint': 'Set the phase, objects and levels yourself',
  'step.phase': 'Phase',
  'phase.title': 'What stage is the project at?',
  'phase.intro':
    'The phase decides which documents belong here and which do not yet. You can switch it later.',
  'step.objects': 'Objects',
  'step.levels': 'How it divides',
  'step.drawings': 'Drawings on each',
  'step.work': 'Work to raise',

  // --- The objektová skladba ------------------------------------------------------------
  //
  // Nothing here offers a list to tick. The statute fixes eight bands; what goes inside them
  // the architect writes during the project, so the copy says the step can wait rather than
  // implying an empty list is unfinished work.
  'objects.title': 'Stavebné objekty and prevádzkové súbory',
  'objects.intro':
    'The numbered parts a permit decision names, such as SO 108 or PS 01. Most of them are written later, as the project takes shape, so this step is worth filling in only for the ones you already know.',
  'objects.explains': 'An object is a separate structure in the project, such as SO 01. Drawings can be filed under it.',
  'objects.empty.title': 'No objects yet',
  'objects.empty':
    'No objects yet, and that is a normal way to start. A drawing does not need one, and a situation drawing never belongs to one.',
  'objects.table': 'Objects in this project',
  'objects.column.number': 'Number',
  'objects.column.name': 'Name',
  'objects.column.code': 'File code',
  'objects.column.actions': 'Actions',
  'objects.add': 'Add an object',
  'objects.addChild': 'Add a sub-object',
  'objects.depthReached': 'Deepest level reached',
  'objects.under': 'Inside {parent}',
  'objects.kind.so': 'Stavebný objekt',
  'objects.kind.ps': 'Prevádzkový súbor',
  'objects.field.number': 'Number',
  'objects.field.label': 'Name',
  'objects.field.category': 'Band',
  'objects.field.fileCode': 'File code',
  'objects.categoryNone': 'No band',
  'objects.fileCodeHint':
    'Position 5 of the file name, such as S01 or P01. It is suggested, editable, and stops being editable once a drawing has been named from it.',
  'objects.main': 'This is the main structure',
  'objects.mainHint': 'One per project. It can be moved to another object later.',
  'objects.mainTag': 'Main structure',
  'objects.makeMain': 'Make this the main structure',
  'objects.locked': 'Number is on a published sheet',
  'objects.archive': 'Archive',
  'objects.save': 'Add',
  'objects.skipStep': 'Add objects later',
  'objects.band.100': 'Site preparation',
  'objects.band.200': 'Buildings',
  'objects.band.300': 'Water management',
  'objects.band.500': 'Power',
  'objects.band.600': 'Low current',
  'objects.band.700': 'Transport',
  'objects.band.800': 'Heat',
  'objects.band.900': 'Landscaping',

  // --- Step 1 -------------------------------------------------------------------------
  'levels.title': 'How the project divides',
  'levels.intro':
    'Each row is one level of the project, inside the one above it. Three blocks of eight floors is two rows, not twenty-seven.',
  'levels.addsOnly':
    'This adds to the project. Nothing you do here moves or removes a level that already exists.',
  'levels.estimate': '{count, plural, one {# level} other {# levels}} so far.',

  // --- Step 2 -------------------------------------------------------------------------
  'drawings.title': 'Which drawings each level carries',
  'drawings.intro':
    'One empty drawing per type, on every level of that row. A hundred and forty-four units with no drawings on them is still four hundred and thirty-two things to create by hand.',
  'drawings.estimate': '{count, plural, one {# empty drawing} other {# empty drawings}} as well.',
  'drawings.none':
    'No drawings yet. The levels alone are a complete answer, and you can add drawings to them any time.',

  // --- The check ----------------------------------------------------------------------
  'check.title': 'What this will create',
  'check.intro':
    'These numbers are counted from the project itself, not from the form. A level’s code becomes part of the address of every drawing under it, and that address locks on the first published version, so it is worth reading now.',

  // --- Step 3 -------------------------------------------------------------------------
  'work.title': 'The work to raise on it',
  'work.created': '{count, plural, one {# row created} other {# rows created}}.',
  'work.intro':
    'One task per level, for each kind of work you pick. You can skip this and raise the work later from the Tasks tab.',
  'work.noTypes.explains': 'A kind of work is a task type the studio uses on every project.',
  'work.noTypes.title': 'No kinds of work written down',
  'work.noTypes':
    'Your studio has not written down the kinds of work it does yet, so there is nothing to raise. That list lives in Studio settings and is written once.',
  'work.noTypesAction': 'Open Studio settings',
  'work.raise': 'Choose the work',

  // --- The end ------------------------------------------------------------------------
  'done.title': 'The project has a shape',
  'done.body': 'Drawings can be uploaded against it now, and it can be added to at any time.',

  // --- The shape editor (phase 2) -----------------------------------------------------
  'shape.pick': 'The levels of this project',
  // The action card's heading when nothing is chosen. The card is drawn either way, so this
  // string exists to stop a heading from being empty rather than to be read twice.
  'shape.none': 'No level chosen',
  // The rail's two edge sentences. `empty` is a project whose tree holds nothing but its own
  // root; `gone` is a selection the rail can no longer name, which here means somebody else
  // removed the level while this screen was open.
  'shape.empty': 'This project has no levels yet.',
  'shape.gone': 'That level is gone',
  'shape.under': '{count, plural, one {# drawing} other {# drawings}}',
  'shape.underNone': 'no drawings',

  'op.add': 'Add levels inside it',
  'op.move': 'Move it',
  'op.clone': 'Copy it',
  'op.wrap': 'Put a level above it',

  'move.intro':
    'The level moves with everything under it. Nothing is deleted, and no drawing changes hands.',
  'move.destination': 'Move it under',
  'move.destinationNone': 'Pick a level',
  'move.already': 'It is already there.',
  // Exact, both of them: a subtree with no drawings has no addresses to move, and a move that
  // keeps its parent changes the order and not the address.
  'move.noAddresses': 'No drawings are filed under this level, so no address changes.',
  // **An upper bound, and worded as one.** `PATCH /hierarchy` has no dry run, and a level's
  // address is rendered from the ancestors the naming template happens to spend, so some of
  // these will not move at all. Saying "up to" is the difference between a number a person can
  // trust and a number that is usually too big.
  'move.upTo':
    '{count, plural, one {# drawing is} other {# drawings are}} filed under this level. Moving it can re-render up to that many addresses. You will see the exact number once the move is done.',
  'move.action': 'Move it',
  'move.done':
    '{count, plural, one {Moved. # address changed} other {Moved. # addresses changed}}.',
  'move.doneNone': 'Moved. No address changed.',

  'wrap.intro':
    'A new level above these, holding everything they hold. Only levels that sit beside this one can go inside it.',
  'wrap.alone': 'This level has nothing beside it, so the new one will hold it alone.',
  'wrap.action': 'Name the new level',

  'action.next': 'Next',
  'action.back': 'Back',
  'action.check': 'See what this creates',
  'action.create': '{count, plural, one {Create # row} other {Create # rows}}',
  'action.notNow': 'Set up later',
  'action.finish': 'Finish',
  'action.close': 'Close',
  'action.openDrawings': 'Open the drawings',
  'action.retry': 'Try again',
  'action.addMore': 'Add more levels',

  'error.refused': 'That plan was refused.',
} as const;

export type ProjectSetupMessageKey = keyof typeof projectSetupEn;
